import { genId } from '../../core/services/genId';
import { ValidationRule } from '../../common/models/flow.dto';
import RuleTreeBuilder from './rule.tree';
import { ValidatorPatterns } from '../../common/classes/validator-patterns';

export class ValidationRuleGenerator {
    /**
     * Generates a Required Rule for a provided context
     * @param context Context Target
     */
    public generateRequiredRule(context: string): ValidationRule {
        return this.generateRule('generated-required', context, ValidatorPatterns.RequiredText, 'Field is required.');
    }

    public generateRequiredRuleLineBreak(context: string): ValidationRule {
        return this.generateRule('generated-required', context, ValidatorPatterns.RequiredTextLineBreak, 'Field is required.');
    }

    public generateRequiredObject(context: string): ValidationRule {
        const builder = new RuleTreeBuilder();
        const object = builder.var(context);
        const result = builder.if(builder.isNull(object), builder.false(), builder.true()); 

        return {
            name: "generated-required-obj",
            ruleId: genId(),
            expression: "",
            message: "Field is required.",
            parseTree: result,
            key: ""
        } as ValidationRule;
    }     

    public generateRequiredFile(context: string): ValidationRule {
        const builder = new RuleTreeBuilder();

        const patternNode = builder.literal('');
        const fileVal = builder.var(`${context}.files`);
        const arrLength = builder.func("LEN", [fileVal]);
        const zeroVal = builder.literal(0);

        const result = builder.if(builder.eq(arrLength, zeroVal), builder.false(), builder.true());

        return {
            name: "generated-required-file",
            ruleId: genId(),
            expression: "",
            message: "Field is required.",
            parseTree: result,
            key: ""
        } as ValidationRule;
    }

    public generateRequiredChoiceFile(context: string): ValidationRule {
        const builder = new RuleTreeBuilder();       
        const conditions = [];
        const optionVal = builder.var(`${context}.value`);
        const emptyPattern = builder.literal('');
        const uploadPattern = builder.literal('upload');
        const fileVal = builder.var(`${context}.files`);
        const arrLength = builder.func("LEN", [fileVal]);
        const zeroVal = builder.literal(0);
        const emptyWhen = builder.when(builder.eq(optionVal, emptyPattern), builder.false());
        conditions.push(emptyWhen);
        const uploadWhen = builder.when(builder.and(builder.eq(optionVal, uploadPattern), builder.eq(arrLength, zeroVal)), builder.false());
        conditions.push(uploadWhen);
        const result = builder.case(conditions, builder.true());
        return {
            name: "generated-required-choice-file",
            ruleId: genId(),
            expression: "",
            message: "Field is required.",
            parseTree: result,
            key: ""
        } as ValidationRule;

    }

    /**
     * Generates a Regex Rule for the provided context and pattern
     * @param context Context Target
     * @param regexPattern Regex Pattern
     */
    public generateRegexRule(context: string, regexPattern: string): ValidationRule {
        return this.generateRule('generated-regex', context, regexPattern, 'Field does not meet the required pattern requirements.');
    }

    /**
     * Generates a Rule for Minimum value
     * @param context Context Target
     * @param minValue Minimum Value
     * TODO: Need string functions from expression language
     */
    public generateMinimumRule(context: string, minValue: number): ValidationRule {
        return this.generateRule('generated-minValue', context, `^.{${minValue},}$`, `Field does not meet the required minimum length requirements.`);
    }

    /**
     * Generates a Rule for Maximum value
     * @param context Context Target
     * @param maxValue Maximum Value
     * TODO: Need string functions from expression language
     */
    public generateMaximumRule(context: string, maxValue: number): ValidationRule {
        return this.generateRule('generated-maxValue', context, `^.{0,${maxValue}}$`, `Field does not meet the required maximum length requirements.`);
    }

    public generateSignatureRule(context: string, confirmation: string): ValidationRule {
        const builder = new RuleTreeBuilder();
        const agreeNode = builder.var(`${context}.agreement`);
        const agreement = builder.literal(confirmation);
        const required = this.generateRequiredRule(`${context}.value`).parseTree;
        const agLogic = builder.eq(agreeNode, agreement);
        const logic = builder.and(required, agLogic);
        return {
            name: "generated-signature",
            ruleId: genId(),
            expression: "",
            message: "Signature and affirmation of agreement are required.",
            parseTree: logic,
            key: ""
        };
    }

    /**
     * Helper Method to generate regular expression rules
     * @param context Context Target
     * @param regex Regular Expression
     */
    public generateRule(name: string, context: string, regex: string, message: string): ValidationRule {
        const builder = new RuleTreeBuilder();

        const contextNode = builder.var(context);
        const patternNode = builder.literal(regex);
        const regexNode = builder.regexMatch(contextNode, patternNode);
        const conditionNode = builder.ne(regexNode, builder.false());
        const ifNode = builder.if(conditionNode, builder.true(), builder.false());

        return {
            name: name,
            ruleId: genId(),
            expression: '',
            message: message,
            parseTree: ifNode,
            key: ''
        };
    }

    public generateLatitudeRule(context: string): ValidationRule {
        const builder = new RuleTreeBuilder();
        const contextNode = builder.var(context);
        const maxRule = builder.gte(builder.literal(90), contextNode);
        const minRule = builder.lte(builder.literal(-90), contextNode);       
        let regex = this.generateRegexRule(context, "^(-?\\d+(\\.\\d{0,6})?|\\.?\\d{0,6})$");
        const andNode = builder.and(maxRule, minRule);
        const andP2 = builder.and(andNode, regex.parseTree);
        const ifNode = builder.if(andP2, builder.true(), builder.false());
        return {
            name: "latitude-validation",
            ruleId: genId(),
            expression: "",
            message: "Latitude must be greater than or equal to -90, less than or equal to 90, and have no more than 6 decimal places.",
            parseTree: ifNode,
            key: ""
        };
    }

    public generateLongitudeRule(context: string): ValidationRule {
        const builder = new RuleTreeBuilder();
        const contextNode = builder.var(context);
        const maxRule = builder.gte(builder.literal(180), contextNode)
        const minRule = builder.lte(builder.literal(-180), contextNode);
        let regex = this.generateRegexRule(context, "^(-?\\d+(\\.\\d{0,6})?|\\.?\\d{0,6})$");
        const andNode = builder.and(maxRule, minRule);
        const andP2 = builder.and(andNode, regex.parseTree);
        const ifNode = builder.if(andP2, builder.true(), builder.false());
        return {
            name: "longitude-validation",
            ruleId: genId(),
            expression: "",
            message: "Longitude must be greater than or equal to -180, less than or equal to 180, and have no more than 6 decimal places.",
            parseTree: ifNode,
            key: ""
        };
    }
}